import type {
  CashDenomination,
  createTreasuryTransactionSchema,
  TreasuryAccountKind,
  TreasuryFlow,
} from "@station/contracts";
import type { z } from "zod";

/** Kuruş dizgesi → bigint. Boş / bozuk değer 0 sayılır; float'a HİÇ çevrilmez (AGENTS.md §3.2). */
export function minorOf(value: string | null | undefined): bigint {
  if (value === null || value === undefined || value === "") return 0n;
  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
}

export function sumMinor(values: readonly (string | null | undefined)[]): bigint {
  return values.reduce<bigint>((total, value) => total + minorOf(value), 0n);
}

// ── Kasa sayımı ──────────────────────────────────────────────────────────────
/** Küpür satırları: değer × adet toplamı (kuruş). Adet negatif ya da NaN ise satır sayılmaz. */
export function denominationTotal(
  rows: readonly { value: string; quantity: number | null }[] | Readonly<Record<string, number | null>>,
): bigint {
  const list = Array.isArray(rows)
    ? (rows as readonly { value: string; quantity: number | null }[])
    : Object.entries(rows as Record<string, number | null>).map(([value, quantity]) => ({ value, quantity }));
  return list.reduce<bigint>((total, row) => {
    const quantity = row.quantity;
    if (quantity === null || !Number.isInteger(quantity) || quantity <= 0) return total;
    return total + minorOf(row.value) * BigInt(quantity);
  }, 0n);
}

/** Yalnızca adedi girilmiş küpürler gövdeye girer; sunucu en az bir satır ister. */
export function denominationPayload(quantities: Readonly<Record<string, number | null>>): CashDenomination[] {
  return Object.entries(quantities)
    .filter(([, quantity]) => quantity !== null && Number.isInteger(quantity) && quantity > 0)
    .map(([value, quantity]) => ({ value, quantity: quantity as number }));
}

/** Sayılan − sistem: pozitif fazla, negatif açık. */
export function countDifference(countedTotal: bigint, systemBalance: string | null | undefined): bigint {
  return countedTotal - minorOf(systemBalance);
}

// ── Karşı hesap satırları ────────────────────────────────────────────────────
export interface OffsetLineState {
  /** Yalnızca ekranda yaşayan satır kimliği (React anahtarı ve hata bağlama için). */
  id: string;
  key: string;
  /** Anahtar parametresi (ör. `VAT_OUTPUT:VAT_STANDARD` içindeki `VAT_STANDARD`). */
  param: string;
  amount: string | null;
  stationId: string;
  description: string;
}

export function blankOffsetLine(id: string, key = "EXPENSE"): OffsetLineState {
  return { id, key, param: "", amount: null, stationId: "", description: "" };
}

/** Belge tutarı − karşı satır toplamı. 0 ise belge dengelidir; kullanıcıya canlı gösterilir. */
export function offsetDifference(amount: string | null, lines: readonly OffsetLineState[]): bigint {
  return minorOf(amount) - sumMinor(lines.map((line) => line.amount));
}

export function offsetKeyRef(line: Pick<OffsetLineState, "key" | "param">): string {
  const param = line.param.trim();
  return param ? `${line.key}:${param}` : line.key;
}

// ── Hareket formu ────────────────────────────────────────────────────────────
export const TREASURY_FORM_TYPES = [
  "CASH_IN",
  "CASH_OUT",
  "BANK_IN",
  "BANK_OUT",
  "TRANSFER",
  "BANK_FEE",
  "INTEREST_INCOME",
  "INTEREST_EXPENSE",
  "OPENING",
] as const;
export type TreasuryFormType = (typeof TREASURY_FORM_TYPES)[number];

/** Transfer kalıpları: kullanıcı "bankaya yatırma" der, form kaynak/hedef türünü kendisi kurar. */
export const TRANSFER_PRESETS = {
  CASH_TO_BANK: { from: "CASH", to: "BANK" },
  BANK_TO_CASH: { from: "BANK", to: "CASH" },
  CASH_TO_CASH: { from: "CASH", to: "CASH" },
  BANK_TO_BANK: { from: "BANK", to: "BANK" },
} as const satisfies Record<string, { from: TreasuryAccountKind; to: TreasuryAccountKind }>;
export type TransferPreset = keyof typeof TRANSFER_PRESETS;
export const TRANSFER_PRESET_KEYS = Object.keys(TRANSFER_PRESETS) as TransferPreset[];

export interface TreasuryTxnFormState {
  txnType: TreasuryFormType;
  documentDate: string | null;
  amount: string | null;
  description: string;
  docNo: string;
  stationId: string;
  /** CASH_IN / CASH_OUT */
  cashRegisterId: string;
  /** BANK_IN / BANK_OUT / BANK_FEE / INTEREST_* */
  bankAccountId: string;
  /** TRANSFER */
  transferPreset: TransferPreset;
  fromId: string;
  toId: string;
  /** OPENING */
  openingKind: TreasuryAccountKind;
  openingId: string;
  openingFlow: TreasuryFlow;
  offsetLines: OffsetLineState[];
}

export function blankTreasuryTransaction(
  txnType: TreasuryFormType,
  today: string,
  lineId: string,
): TreasuryTxnFormState {
  return {
    txnType,
    documentDate: today,
    amount: null,
    description: "",
    docNo: "",
    stationId: "",
    cashRegisterId: "",
    bankAccountId: "",
    transferPreset: "CASH_TO_BANK",
    fromId: "",
    toId: "",
    openingKind: "CASH",
    openingId: "",
    openingFlow: "IN",
    offsetLines: [blankOffsetLine(lineId, defaultOffsetKey(txnType))],
  };
}

/** Tahsilat gelir, tediye gider doğurur: kullanıcı ilk satırı çoğunlukla değiştirmeden kullanır. */
export function defaultOffsetKey(txnType: TreasuryFormType): string {
  return txnType === "CASH_IN" || txnType === "BANK_IN" ? "OTHER_INCOME" : "EXPENSE";
}

export const needsOffsetLines = (txnType: TreasuryFormType): boolean =>
  txnType === "CASH_IN" || txnType === "CASH_OUT" || txnType === "BANK_IN" || txnType === "BANK_OUT";
export const needsCashRegister = (txnType: TreasuryFormType): boolean =>
  txnType === "CASH_IN" || txnType === "CASH_OUT";
export const needsBankAccount = (txnType: TreasuryFormType): boolean =>
  txnType === "BANK_IN" ||
  txnType === "BANK_OUT" ||
  txnType === "BANK_FEE" ||
  txnType === "INTEREST_INCOME" ||
  txnType === "INTEREST_EXPENSE";

export type TreasuryTxnPayload = z.input<typeof createTreasuryTransactionSchema>;

/**
 * Ekran durumunu sözleşme gövdesine çevirir. Türe AİT OLMAYAN (ekranda görünmeyen) alanlar gövdeye hiç girmez —
 * doğrulama hatası gizli bir alana düşemez (Prompt 07 dersi). Boş isteğe bağlı alanlar null olur.
 */
export function toTreasuryPayload(companyId: string, state: TreasuryTxnFormState): TreasuryTxnPayload {
  const base = {
    companyId,
    documentDate: state.documentDate ?? "",
    amount: minorOf(state.amount).toString(),
    description: state.description.trim(),
    docNo: state.docNo.trim() || null,
    stationId: state.stationId || null,
  };
  const offsetLines = state.offsetLines.map((line) => ({
    key: offsetKeyRef(line),
    amount: minorOf(line.amount).toString(),
    stationId: line.stationId || null,
    businessUnit: null,
    counterpartyId: null,
    description: line.description.trim() || null,
  }));

  switch (state.txnType) {
    case "CASH_IN":
    case "CASH_OUT":
      return { ...base, txnType: state.txnType, cashRegisterId: state.cashRegisterId, offsetLines };
    case "BANK_IN":
    case "BANK_OUT":
      return { ...base, txnType: state.txnType, bankAccountId: state.bankAccountId, offsetLines };
    case "TRANSFER": {
      const preset = TRANSFER_PRESETS[state.transferPreset];
      return {
        ...base,
        txnType: "TRANSFER",
        from: { kind: preset.from, id: state.fromId },
        to: { kind: preset.to, id: state.toId },
      };
    }
    case "OPENING":
      return {
        ...base,
        txnType: "OPENING",
        account: { kind: state.openingKind, id: state.openingId },
        flow: state.openingFlow,
      };
    default:
      return { ...base, txnType: state.txnType, bankAccountId: state.bankAccountId };
  }
}

export const TREASURY_TXN_FIELDS = [
  "documentDate",
  "amount",
  "description",
  "docNo",
  "stationId",
  "cashRegisterId",
  "bankAccountId",
  "from",
  "to",
  "account",
  "flow",
] as const;
export type TreasuryTxnField = (typeof TREASURY_TXN_FIELDS)[number];

export interface TreasuryTxnErrors {
  fields: Partial<Record<TreasuryTxnField, string>>;
  /** Karşı satır kimliği → ileti (`offsetLines.N.amount`). */
  lines: Record<string, string>;
  general: string[];
}

/**
 * Zod (`["offsetLines",1,"amount"]`) ve sunucu (`"offsetLines.1.amount"`) yollarını ekrandaki alana bağlar.
 * `lineIds[n]` gönderilen n. satırın ekran kimliğidir. Bağlanamayan hiçbir ileti kaybolmaz; `general`'e düşer.
 */
export function collectTreasuryErrors(
  issues: ReadonlyArray<{ path: string | ReadonlyArray<PropertyKey>; message: string }>,
  lineIds: readonly string[],
): TreasuryTxnErrors {
  const errors: TreasuryTxnErrors = { fields: {}, lines: {}, general: [] };
  for (const issue of issues) {
    const parts = (typeof issue.path === "string" ? issue.path.split(".") : issue.path.map(String)).filter(
      (part, index) => !(index === 0 && part === "body"),
    );
    const [first, second] = parts;
    if (first === "offsetLines" && second !== undefined && lineIds[Number(second)]) {
      errors.lines[lineIds[Number(second)]!] ??= issue.message;
      continue;
    }
    if (first !== undefined && (TREASURY_TXN_FIELDS as readonly string[]).includes(first)) {
      errors.fields[first as TreasuryTxnField] ??= issue.message;
      continue;
    }
    if (!errors.general.includes(issue.message)) errors.general.push(issue.message);
  }
  return errors;
}

// ── POS gün sonu ─────────────────────────────────────────────────────────────
export interface PosBatchLineState {
  id: string;
  cardType: string;
  installments: number;
  grossAmount: string | null;
}

export function posBatchGross(lines: readonly PosBatchLineState[]): bigint {
  return sumMinor(lines.map((line) => line.grossAmount));
}

/**
 * Terminalin tarihli oranlarından satırın oranını seçer: kart türü + taksit eşleşen ve iş gününü kapsayan oran.
 * Birden çok aday varsa en GEÇ başlayan kazanır (sunucudaki kuralla aynı).
 */
export function pickCommissionRate(
  rates: readonly {
    cardType: string;
    installments: number;
    rate: string;
    validFrom: string;
    validTo: string | null;
  }[],
  line: Pick<PosBatchLineState, "cardType" | "installments">,
  businessDate: string,
): string | null {
  const candidates = rates
    .filter(
      (rate) =>
        rate.cardType === line.cardType &&
        rate.installments === line.installments &&
        rate.validFrom <= businessDate &&
        (rate.validTo === null || rate.validTo >= businessDate),
    )
    .sort((left, right) => (left.validFrom < right.validFrom ? 1 : -1));
  return candidates[0]?.rate ?? null;
}

// ── Kredi taksitleri ─────────────────────────────────────────────────────────
export interface InstallmentState {
  id: string;
  installmentNo: number;
  dueDate: string | null;
  principalAmount: string | null;
  interestAmount: string | null;
  otherAmount: string | null;
}

export function installmentTotal(row: InstallmentState): bigint {
  return minorOf(row.principalAmount) + minorOf(row.interestAmount) + minorOf(row.otherAmount);
}

export function installmentsTotal(rows: readonly InstallmentState[]): bigint {
  return rows.reduce<bigint>((total, row) => total + installmentTotal(row), 0n);
}

// ── Biçimlendirme ────────────────────────────────────────────────────────────
/** IBAN dörtlü gruplarla okunur: `TR94 0006 2000 0000 0005 1700 02`. */
export function formatIban(iban: string | null | undefined): string {
  if (!iban) return "—";
  return iban
    .replace(/\s+/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

/** Limit doluluk yüzdesi çubuğu 100'ü aşamaz; aşım ayrıca rozetle gösterilir. */
export function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}
